import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toApiError } from '../../api/errors';
import type { Id } from '../../api/types';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorNotice, StaleBanner } from '../../components/ui/Notice';
import { SkeletonBar } from '../../components/ui/Skeleton';
import { useResource } from '../../hooks/useResource';
import { PromoteDialog } from './PromoteDialog';
import { SpaceItemCard } from './SpaceItemCard';
import {
  createSpaceItem,
  fetchSpaceItems,
  fetchSpaces,
  promoteSpaceItem,
  spaceItemsPath,
  updateSpaceItem,
  type PromoteBody,
  type Space,
  type SpaceItem,
} from './spaces-api';

/**
 * SPACES — meeting topics and research items, and the one bridge into dev work.
 *
 * ✅ The endpoints exist as of the API's `93fd61b`. This page previously rendered a named gap; that
 * notice is gone and the data is real.
 *
 * ⛔ WHAT HAS NOT CHANGED: it does not fall back to `GET /tickets`. db/001 is explicit that a space
 * item is deliberately NOT a ticket with a label — no branch, no MR, no sprint — and a tickets-backed
 * "spaces" view would look finished, be the wrong entity, and have nothing to promote.
 *
 * ⚠️ Renders no app chrome. Must be inside `ProjectsProvider` and `VocabularyProvider` — the promote
 * dialog needs the project list and the type vocabulary.
 */
export function SpacesPage() {
  const [params, setParams] = useSearchParams();
  const selectedSpace = params.get('space');

  const spaces = useResource<Space[]>((signal) => fetchSpaces(signal), []);
  const spaceList = useMemo(() => spaces.data ?? [], [spaces.data]);

  // ⭐ Default to the first space only once the list has ARRIVED — before that there is no first
  // space, and writing a guess into the URL would make a shared link point somewhere arbitrary.
  useEffect(() => {
    if (selectedSpace || spaceList.length === 0) return;
    const next = new URLSearchParams(params);
    next.set('space', String(spaceList[0]?.id));
    setParams(next, { replace: true });
  }, [selectedSpace, spaceList, params, setParams]);

  const items = useResource<SpaceItem[]>(
    (signal) => fetchSpaceItems(selectedSpace as Id, signal),
    [selectedSpace],
    { enabled: Boolean(selectedSpace) },
  );

  const space = useMemo(
    () => spaceList.find((entry) => String(entry.id) === selectedSpace) ?? null,
    [spaceList, selectedSpace],
  );

  const [busyId, setBusyId] = useState<Id | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [promoting, setPromoting] = useState<SpaceItem | null>(null);
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');

  const fail = useCallback((id: Id, err: unknown, path: string) => {
    const apiError = toApiError(err, path);
    setRowErrors((previous) => ({
      ...previous,
      [String(id)]: `${apiError.message}${apiError.reason ? ` — ${apiError.reason}` : ''}`,
    }));
  }, []);

  const setState = useCallback(
    async (item: SpaceItem, state: string) => {
      setBusyId(item.id);
      setRowErrors((previous) => {
        const next = { ...previous };
        delete next[String(item.id)];
        return next;
      });
      try {
        await updateSpaceItem(item.id, { state });
        items.reload();
      } catch (err) {
        fail(item.id, err, `/space-items/${item.id}`);
      } finally {
        setBusyId(null);
      }
    },
    [items, fail],
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-[color:var(--color-surface)]">
      <header className="border-b border-[color:var(--color-line)] px-3 py-2 sm:px-4">
        <h1 className="text-base font-semibold">Spaces</h1>
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          Meeting topics and research items — <strong>not tickets</strong>. No branch, no MR, no
          sprint. The one bridge is promotion.
        </p>
      </header>

      {spaceList.length > 0 ? (
        <nav aria-label="Spaces" className="overflow-x-auto border-b border-[color:var(--color-line)]">
          <ul className="flex min-w-max gap-1 px-3 py-2 sm:px-4">
            {spaceList.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  aria-current={String(entry.id) === selectedSpace}
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.set('space', String(entry.id));
                    setParams(next, { replace: true });
                  }}
                  className={`h-8 rounded-full px-3 text-xs font-semibold whitespace-nowrap ${
                    String(entry.id) === selectedSpace
                      ? 'bg-[color:var(--color-ink)] text-[color:var(--color-surface)]'
                      : 'bg-[color:var(--color-line)] text-[color:var(--color-ink-muted)]'
                  }`}
                >
                  {entry.kind === 'meeting' ? '🗣 ' : '🔬 '}
                  {entry.name}
                  {/*
                    ⭐⭐ THE OPEN COUNT, NOT THE TOTAL. The API sends three counts and its own comment
                    says why they are never summed: "a space with 12 items, 4 open and 3 promoted is
                    not 19 of anything". What a reader wants off a tab is how much is still live.
                  */}
                  <span className="ml-1 opacity-70">
                    {entry.open_item_count}/{entry.item_count}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-3 p-3 sm:p-4 empty:hidden">
          {spaces.error && !spaces.isStale ? (
            <ErrorNotice error={spaces.error} onRetry={spaces.reload} />
          ) : null}
          {spaces.isStale && spaces.error ? (
            <StaleBanner error={spaces.error} loadedAt={spaces.loadedAt} onRetry={spaces.reload} />
          ) : null}
          {items.error && !items.isStale ? (
            <ErrorNotice error={items.error} onRetry={items.reload} />
          ) : null}
          {items.isStale && items.error ? (
            <StaleBanner error={items.error} loadedAt={items.loadedAt} onRetry={items.reload} />
          ) : null}
        </div>

        {spaces.isLoading || items.isLoading ? (
          <div role="status" aria-label="Loading spaces" className="space-y-2 px-3 sm:px-4">
            {Array.from({ length: 4 }, (_, index) => (
              <SkeletonBar key={index} className="h-24 w-full" />
            ))}
          </div>
        ) : null}

        {/* ⛔ Only when the request ANSWERED. `EmptyState` is a claim about the data and takes no
            error by construction; gating it on `data` is what keeps that true here. */}
        {spaces.data && spaceList.length === 0 ? (
          <EmptyState
            title="No spaces yet"
            detail="The request succeeded and returned none — an empty list, not a failure. A space is a meeting or a research area; items live inside one, and only an admin can create one."
          />
        ) : null}

        {space && items.data && items.data.length === 0 ? (
          <EmptyState
            title={`Nothing in ${space.name} yet`}
            detail="The request succeeded and returned none."
          />
        ) : null}

        {items.data && items.data.length > 0 ? (
          <ul className="space-y-2 px-3 pb-4 sm:px-4">
            {items.data.map((item) => (
              <SpaceItemCard
                key={item.id}
                item={item}
                kind={space?.kind ?? 'research'}
                busy={busyId === item.id}
                error={rowErrors[String(item.id)] ?? null}
                onPromote={() => {
                  setPromoteError(null);
                  setPromoting(item);
                }}
                onSetState={(state) => void setState(item, state)}
              />
            ))}
          </ul>
        ) : null}

        {space && items.data ? (
          <form
            className="flex flex-wrap items-end gap-2 border-t border-[color:var(--color-line)] px-3 py-3 sm:px-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!newTitle.trim()) return;
              void (async () => {
                setBusyId(space.id);
                try {
                  await createSpaceItem(space.id, { title: newTitle.trim() });
                  setNewTitle('');
                  items.reload();
                  // ⭐ The tab counts live on the SPACE, so they are stale the moment an item lands.
                  spaces.reload();
                } catch (err) {
                  fail(space.id, err, spaceItemsPath(space.id));
                } finally {
                  setBusyId(null);
                }
              })();
            }}
          >
            <div className="min-w-0 flex-1">
              <label className="block text-xs font-semibold" htmlFor="new-space-item">
                New {space.kind === 'meeting' ? 'topic' : 'research item'} in {space.name}
              </label>
              <input
                id="new-space-item"
                value={newTitle}
                onChange={(event) => setNewTitle(event.target.value)}
                className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={busyId === space.id || !newTitle.trim()}
              className="h-9 rounded bg-[color:var(--color-accent)] px-3 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50"
            >
              Add
            </button>
            {rowErrors[String(space.id)] ? (
              <p role="alert" className="w-full text-xs font-semibold text-red-700 dark:text-red-300">
                {rowErrors[String(space.id)]}
              </p>
            ) : null}
          </form>
        ) : null}
      </div>

      {promoting ? (
        <PromoteDialog
          item={promoting}
          busy={busyId === promoting.id}
          error={promoteError}
          onCancel={() => setPromoting(null)}
          onConfirm={(body: PromoteBody) => {
            void (async () => {
              setBusyId(promoting.id);
              setPromoteError(null);
              try {
                await promoteSpaceItem(promoting.id, body);
                setPromoting(null);
                // ⛔ The card is NOT marked promoted optimistically. A refused promotion — 403 for
                // non-membership, 409 for an item already promoted — would leave a card claiming a
                // ticket that was never created, and its link would point at nothing.
                items.reload();
                spaces.reload();
              } catch (err) {
                const apiError = toApiError(err, `/space-items/${promoting.id}/promote`);
                setPromoteError(
                  `${apiError.message}${apiError.reason ? ` — ${apiError.reason}` : ''}`,
                );
              } finally {
                setBusyId(null);
              }
            })();
          }}
        />
      ) : null}
    </div>
  );
}

export default SpacesPage;
